import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Radio, Search, SlidersHorizontal } from "lucide-react";
import type { ChannelOverride } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { forwardingErrorMessage } from "@/lib/forwarding";
import { gateReasonFromError, type GateReason } from "@/lib/plan-gates";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";
import {
  useUpdateForwardingChannel,
  type ChannelRole,
  type ForwardingChannel,
} from "@/lib/hooks/useForwarding";
import { InlineError, PlatformBadge, Segmented } from "./controls";

type PlatformFilter = "all" | "telegram" | "whatsapp";
type RoleFilter = "all" | ChannelRole;

interface ChannelListProps {
  readonly channels: readonly ForwardingChannel[];
  readonly overrides: readonly ChannelOverride[];
  readonly maxDestinations: number;
  readonly onOpenOverride: (channel: ForwardingChannel) => void;
}

export function filterChannels(
  channels: readonly ForwardingChannel[],
  query: string,
  platform: PlatformFilter,
  role: RoleFilter,
): ForwardingChannel[] {
  const q = query.trim().toLowerCase();
  return channels.filter(
    (c) =>
      (platform === "all" || c.platform === platform) &&
      (role === "all" || c.role === role) &&
      (q === "" || (c.title ?? "").toLowerCase().includes(q) || c.ref.toLowerCase().includes(q)),
  );
}

const STATUS_LABEL: Readonly<Record<ForwardingChannel["status"], string>> = {
  active: "Ativo",
  paused: "Pausado",
  error_not_member: "Conta saiu do grupo",
};

function StatusToggle({
  status,
  disabled,
  onToggle,
}: {
  status: ForwardingChannel["status"];
  disabled: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={status === "error_not_member" || disabled}
      onClick={onToggle}
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-xs font-medium",
        status === "active"
          ? "border-primary/20 bg-primary/10 text-primary"
          : "border-transparent bg-muted text-muted-foreground",
      )}
      aria-label={`${STATUS_LABEL[status]}: alternar`}
    >
      {STATUS_LABEL[status]}
    </button>
  );
}

/** Aplica um patch no canal e reporta o erro (ou limpa) via onError. */
type ChannelErrorHandler = (message: string | null, gate?: GateReason | null) => void;

function useChannelPatch(
  channel: ForwardingChannel,
  onError: ChannelErrorHandler,
): {
  isPending: boolean;
  run: (patch: { role?: ChannelRole; status?: "active" | "paused" }) => Promise<void>;
} {
  const update = useUpdateForwardingChannel();
  const run = async (patch: {
    role?: ChannelRole;
    status?: "active" | "paused";
  }): Promise<void> => {
    onError(null);
    try {
      await update.mutateAsync({ channel, ...patch });
    } catch (err) {
      onError(forwardingErrorMessage(err), gateReasonFromError(err));
    }
  };
  return { isPending: update.isPending, run };
}

function ChannelName({ channel }: { channel: ForwardingChannel }): JSX.Element {
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-foreground">
        {channel.title ?? "Sem nome"}
      </p>
      <p className="truncate font-mono text-[11px] text-muted-foreground">{channel.ref}</p>
    </div>
  );
}

function ChannelRow({
  channel,
  hasOverride,
  onOpenOverride,
  onError,
}: {
  channel: ForwardingChannel;
  hasOverride: boolean;
  onOpenOverride: () => void;
  onError: ChannelErrorHandler;
}): JSX.Element {
  const { isPending, run } = useChannelPatch(channel, onError);
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:border-primary/25">
      <PlatformBadge platform={channel.platform} />
      <ChannelName channel={channel} />
      <Segmented
        size="sm"
        label={`Papel de ${channel.title ?? channel.ref}`}
        value={channel.role}
        onChange={(role) => void run({ role })}
        options={[
          { value: "origem", label: "Origem" },
          { value: "destino", label: "Destino" },
        ]}
      />
      <StatusToggle
        status={channel.status}
        disabled={isPending}
        onToggle={() => void run({ status: channel.status === "active" ? "paused" : "active" })}
      />
      {channel.role === "origem" && (
        <Button variant="ghost" size="sm" onClick={onOpenOverride}>
          <SlidersHorizontal size={13} />
          {hasOverride ? "Ajustes próprios" : "Ajustes"}
        </Button>
      )}
    </li>
  );
}

function ListHeader({ active, max }: { active: number; max: number }): JSX.Element {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 id="channels-title" className="font-semibold text-foreground">
          Grupos e canais
        </h2>
        <p className="text-xs text-muted-foreground">
          Defina o papel de cada um: origem (monitorado) ou destino (recebe as ofertas).
        </p>
      </div>
      <span
        className={cn(
          "rounded-full border px-3 py-1 font-mono text-xs tabular-nums",
          active >= max
            ? "border-amber-400/30 bg-amber-400/10 text-amber-300"
            : "border-border text-muted-foreground",
        )}
      >
        {active}/{max} destinos
      </span>
    </div>
  );
}

interface ToolbarProps {
  readonly query: string;
  readonly platform: PlatformFilter;
  readonly role: RoleFilter;
  readonly onQuery: (q: string) => void;
  readonly onPlatform: (p: PlatformFilter) => void;
  readonly onRole: (r: RoleFilter) => void;
}

function ListToolbar({
  query,
  platform,
  role,
  onQuery,
  onPlatform,
  onRole,
}: ToolbarProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[12rem] flex-1">
        <Search
          size={14}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          aria-label="Buscar canal"
          placeholder="Buscar por nome ou ID"
          className="pl-8"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </div>
      <Segmented
        label="Plataforma"
        value={platform}
        onChange={onPlatform}
        options={[
          { value: "all", label: "Todas" },
          { value: "telegram", label: "Telegram" },
          { value: "whatsapp", label: "WhatsApp" },
        ]}
      />
      <Segmented
        label="Papel"
        value={role}
        onChange={onRole}
        options={[
          { value: "all", label: "Todos" },
          { value: "origem", label: "Origens" },
          { value: "destino", label: "Destinos" },
        ]}
      />
    </div>
  );
}

function EmptyChannels(): JSX.Element {
  return (
    <div className="rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-8 text-center">
      <Radio size={22} className="mx-auto text-primary" />
      <p className="mt-3 font-semibold text-foreground">Nenhum grupo ou canal ainda</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        Conecte sua conta do Telegram ou WhatsApp em{" "}
        <Link to="/app/settings/integracao" className="text-primary hover:underline">
          Integrações
        </Link>{" "}
        e os grupos aparecem aqui para você escolher origens e destinos.
      </p>
    </div>
  );
}

/** Lista de grupos/canais de todas as contas conectadas, com busca e filtros. */
export function ChannelList({
  channels,
  overrides,
  maxDestinations,
  onOpenOverride,
}: ChannelListProps): JSX.Element {
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [role, setRole] = useState<RoleFilter>("all");
  const [error, setErrorMessage] = useState<string | null>(null);
  const [gate, setGate] = useState<GateReason | null>(null);
  const setError: ChannelErrorHandler = (message, reason = null) => {
    setErrorMessage(message);
    setGate(reason);
  };
  const visible = useMemo(
    () => filterChannels(channels, query, platform, role),
    [channels, query, platform, role],
  );
  const activeDestinations = channels.filter(
    (c) => c.role === "destino" && c.status === "active",
  ).length;
  const overrideKeys = new Set(overrides.map((o) => `${o.platform}:${o.channelRef}`));

  return (
    <section className="space-y-3" aria-labelledby="channels-title">
      <ListHeader active={activeDestinations} max={maxDestinations} />
      <ListToolbar
        query={query}
        platform={platform}
        role={role}
        onQuery={setQuery}
        onPlatform={setPlatform}
        onRole={setRole}
      />
      {gate ? <UpgradePrompt reason={gate} variant="inline" /> : <InlineError message={error} />}
      <ChannelListBody
        total={channels.length}
        visible={visible}
        overrideKeys={overrideKeys}
        onOpenOverride={onOpenOverride}
        onError={setError}
      />
    </section>
  );
}

function ChannelListBody({
  total,
  visible,
  overrideKeys,
  onOpenOverride,
  onError,
}: {
  total: number;
  visible: readonly ForwardingChannel[];
  overrideKeys: ReadonlySet<string>;
  onOpenOverride: (channel: ForwardingChannel) => void;
  onError: ChannelErrorHandler;
}): JSX.Element {
  if (total === 0) return <EmptyChannels />;
  if (visible.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhum canal com esses filtros.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {visible.map((c) => (
        <ChannelRow
          key={`${c.platform}:${c.id}`}
          channel={c}
          hasOverride={overrideKeys.has(`${c.platform}:${c.ref}`)}
          onOpenOverride={() => onOpenOverride(c)}
          onError={onError}
        />
      ))}
    </ul>
  );
}
